import { Breadcrumbs, SkeletonBlock } from "@zapier/design-system";
import {
  getFoldersIndexUrl,
  getFolderUrl,
  getZapsIndexUrl,
  getAssetsIndexUrl,
  getVirtualFolderName,
  isVirtualFolder,
} from "@zapier/utils";
import { FolderAncestor, Member } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";

interface User {
  firstName?: string | null;
  lastName?: string | null;
}
import Link from "next/link";

import styles from "./FolderBreadcrumbs.module.css";

export interface FolderBreadcrumbsProps {
  ancestors: FolderAncestor[] | [];
  currentFolderName: string | undefined;
  isAncestorsLoading?: boolean;
  hasAssetsRoot?: boolean;
  user?: User;
  memberId?: string | null;
  members?: Record<string, Member>;
}

const FolderBreadcrumbs = ({
  ancestors,
  currentFolderName,
  isAncestorsLoading,
  hasAssetsRoot,
  user,
  memberId,
  members,
}: FolderBreadcrumbsProps) => {
  const t = useTranslations("folder-breadcrumbs");

  return (
    <nav aria-label="breadcrumbs" className={styles.breadcrumbWrapper}>
      {isAncestorsLoading && (
        <Breadcrumbs>
          <SkeletonBlock width="100%" height={20} />
        </Breadcrumbs>
      )}
      {!isAncestorsLoading && (
        <Breadcrumbs size="small">
          {hasAssetsRoot ? (
            <Link
              title={t("navigation.assets-link")}
              href={getAssetsIndexUrl()}
            >
              {t("navigation.assets-link")}
            </Link>
          ) : (
            <Link title={t("navigation.zaps-link")} href={getZapsIndexUrl()}>
              {t("navigation.zaps-link")}
            </Link>
          )}
          <Link
            title={t("navigation.folders-link")}
            href={getFoldersIndexUrl()}
          >
            {t("navigation.folders-link")}
          </Link>
          {ancestors.map((ancestor) => {
            const ancestorName = isVirtualFolder(ancestor.id)
              ? getVirtualFolderName(ancestor.id, { user, memberId, members })
              : ancestor.name;
            return (
              <Link
                title={ancestorName}
                href={getFolderUrl(ancestor.id)}
                key={ancestor.id}
              >
                {ancestorName}
              </Link>
            );
          })}
          {!isAncestorsLoading && (
            <span aria-current="page" title={currentFolderName}>
              {currentFolderName}
            </span>
          )}
        </Breadcrumbs>
      )}
    </nav>
  );
};

export default FolderBreadcrumbs;
